import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { FormFieldContext } from '../FormField/FormFieldContext'

/**
 * Bronson Textarea component. Essentially, a `<textarea>` element uses the same markup as {@link Input}.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-textarea.html
 *
 * @param {boolean} [charCounter] - Display a character counter.
 * @param {string} [children] - Textarea content.
 * @param {boolean} [enableMaxLength=true] - Enable `[maxlength]` limitation.
 * @param {boolean} [error] - Render the Textarea as invalid.
 * @param {ErrorMessage} [errorMessage] - Optional {@link ErrorMessage} to render.
 * @param {string} [id] - The `<textarea>` element `[id]` attribute.
 * @param {number} [maxCounter] - The maximum character number. Displayed in the char counter.
 * @param {string} [modifierStates] - Additional CSS classes for the `<textarea>` element.
 * @param {function} [onChange] - Callback function when the `<textarea>` value changes.
 * @param {boolean} [success] - Render the Textarea as valid.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element. Replaces 'attr' in Bronson template.
 * @returns {JSX.Element} - The Textarea component.
 * @constructor
 */
export function Textarea({
  charCounter,
  children,
  enableMaxLength = true,
  error,
  errorMessage,
  id,
  maxCounter,
  modifierStates,
  onChange,
  success,
  testId,
  ...otherProps
}) {
  /**
   * Track the current character count.
   */
  const [currentCount, setCurrentCount] = useState(0)

  /**
   * Track the internal state via {@link FormFieldContext}.
   * @type {object}
   */
  const context = useContext(FormFieldContext)

  /**
   * 1. Update the context state and mark the component as active.
   * 2. Set the {@link currentCount} based on the character length of the `<textarea>`.
   */
  useEffect(() => {
    if (context?.setElementActive) {
      context.setElementActive(!!children)
    }
    setCurrentCount(children?.length ?? 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children])

  const wrapperClassNameList = classNames('c-input', 'c-input--textarea', {
    'is-error': (charCounter && maxCounter && currentCount >= maxCounter) ?? error,
    'is-success': success,
  }).trim()

  const textareaClassNameList = classNames('c-input__input', {
    'is-error': (charCounter && maxCounter && currentCount >= maxCounter) ?? error,
    'is-success': success,
    [`${modifierStates} `]: modifierStates,
  }).trim()

  const counterClassNameList = classNames('c-input__counter', {
    'c-input__counter--alert': currentCount >= maxCounter,
  }).trim()

  /**
   * Conditionally render an `[aria-describedby]` attribute,
   * where {@link error} with {@link errorMessage} takes precedence over {@link charCounter}.
   * @type {string|null}
   */
  // eslint-disable-next-line no-nested-ternary
  const describedByAttr = error && errorMessage ? `error-${id}` : charCounter ? `${id}-counter` : null

  return (
    <>
      <div className={wrapperClassNameList}>
        {charCounter && maxCounter && (
          <div className={counterClassNameList} id={`${id}-counter`} aria-live="polite">
            <span className="u-visually-hidden">Text length:</span>
            {currentCount}
            <span aria-hidden="true">/</span>
            <span className="u-visually-hidden">of</span>
            {maxCounter}
            <span className="u-visually-hidden">allowed characters</span>
          </div>
        )}
        <textarea
          data-testid={testId}
          className={textareaClassNameList}
          id={id}
          cols="30"
          rows="10"
          onChange={(event) => {
            charCounter && setCurrentCount(event.target.value.length)
            return [
              onChange && onChange(event),
              context.setElementActive && context.setElementActive(!!event.target.value),
            ]
          }}
          aria-describedby={describedByAttr}
          {...(enableMaxLength && maxCounter ? { maxLength: maxCounter } : {})}
          {...otherProps}
          defaultValue={children}
        />
      </div>
      {errorMessage}
    </>
  )
}

Textarea.propTypes = {
  charCounter: PropTypes.bool, // Adds a character counter to the Textarea.
  children: PropTypes.string, // Textarea content.
  enableMaxLength: PropTypes.bool, // Enable max length limitation, see HTML `[maxlength]` attribute.
  errorMessage: PropTypes.element, //  Bronson template: 'error-message', use {@link ErrorMessage} component.
  id: PropTypes.string, // Bronson template: 'id'.
  maxCounter: PropTypes.number, // Max counter of the Textarea.
  modifierStates: PropTypes.string, // Bronson template: 'modifier-states'.
  onChange: PropTypes.func,
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /* Convenience Props */
  error: PropTypes.bool, // Convenience prop for `.is-error` (Bronson template: 'error').
  success: PropTypes.bool, // Convenience prop for `.is-success` (Bronson template: 'modifier-states').
}
